import React from 'react';
import { View, Text, StyleSheet, ScrollView, Linking, TouchableOpacity } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ADMIN_COLORS, USER_TYPES } from '@/constants/admin';
import { IResidentDetailsResponse } from '@/types/admin/residentDetails';
import { ContactRow } from '@/components/common/admin/ContactRow';
import { TransactionHistorySection } from '../common/TransactionHistorySection';

interface ResidentDetailsInfoProps {
  resident: Pick<IResidentDetailsResponse, 'phoneNumber' | 'email' | 'linkedAccounts' | 'roommates' | 'accountId' | 'leaseId' | 'firstName' | 'lastName'>;
  leaseCustomerId?: number;
  leaseId?: number;
}

export const ResidentDetailsInfo: React.FC<ResidentDetailsInfoProps> = ({ resident, leaseCustomerId, leaseId }) => {
  const handlePhonePress = (phoneNumber: string) => {
    Linking.openURL(`tel:${phoneNumber}`);
  };

  const handleEmailPress = (email: string) => {
    Linking.openURL(`mailto:${email}`);
  };

  const convertAccountType = (type: number): string => {
    switch (type) {
      case 1:
        return USER_TYPES[1]?.toUpperCase() || 'ADMIN';
      case 2:
        return USER_TYPES[2]?.toUpperCase() || 'RESIDENT';
      case 3:
        return USER_TYPES[3]?.toUpperCase() || 'STAFF';
      default:
        return 'UNKNOWN';
    }
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || '??';
  };

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Contact Info Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Contact Info</Text>
        
        {/* Phone */}
        <ContactRow
          kind="phone"
          label="PHONE"
          value={resident.phoneNumber}
          onPress={() => resident.phoneNumber && handlePhonePress(resident.phoneNumber)}
        />

        {/* Email */}
        <ContactRow
          kind="email"
          label="EMAIL"
          value={resident.email}
          onPress={() => resident.email && handleEmailPress(resident.email)}
        />
      </View>

      {/* Linked Accounts Section */}
      {resident.linkedAccounts && resident.linkedAccounts.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Linked Accounts</Text>
          {resident.linkedAccounts.map((account: any, index: number) => (
            <View key={account.data?.account?.id || index} style={styles.accountRow}>
              <View style={styles.avatarSmall}>
                <Text style={styles.avatarSmallText}>
                  {getInitials(account.data?.account?.firstName, account.data?.account?.lastName)}
                </Text>
              </View>
              <View style={styles.accountInfo}>
                <Text style={styles.accountType}>
                  {convertAccountType(account.data?.account?.type)}
                </Text>
                <Text style={styles.accountName}>
                  {account.data?.account?.firstName && account.data?.account?.lastName
                    ? `${account.data.account.firstName} ${account.data.account.lastName}`
                    : 'Connected Account'}
                </Text>
              </View>
            </View>
          ))}
        </View>
      )}

      {/* Roommates Section */}
      {resident.roommates && resident.roommates.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Roommates</Text>
          {resident.roommates.map((roommate: any, index: number) => (
            <View key={roommate.data?.account?.id || index} style={styles.accountRow}>
              <View style={styles.avatarSmall}>
                <Text style={styles.avatarSmallText}>
                  {getInitials(roommate.data?.account?.firstName, roommate.data?.account?.lastName)}
                </Text>
              </View>
              <View style={styles.accountInfo}>
                <Text style={styles.accountType}>
                  {roommate.data?.account?.unitNumber || 'ROOMMATE'}
                </Text>
                <Text style={styles.accountName}>
                  {roommate.data?.account?.firstName && roommate.data?.account?.lastName
                    ? `${roommate.data.account.firstName} ${roommate.data.account.lastName}`
                    : 'Connected Account'}
                </Text>
              </View>
            </View>
          ))}
        </View>
      )}

      {/* Transaction History Section */}
      <TransactionHistorySection 
        accountId={leaseCustomerId || resident.accountId} 
        leaseId={leaseId || resident.leaseId}
        residentName={
          resident.firstName && resident.lastName
            ? `${resident.firstName} ${resident.lastName}`
            : undefined
        }
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  section: {
    backgroundColor: ADMIN_COLORS.SURFACE,
    paddingHorizontal: 16,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 16,
  },
  contactRow: {},
  iconContainer: {},
  contactInfo: {},
  contactLabel: {},
  contactValue: {},
  accountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  avatarSmall: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: ADMIN_COLORS.PRIMARY + '20',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatarSmallText: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.PRIMARY,
  },
  accountInfo: {
    flex: 1,
  },
  accountType: {
    fontSize: 11,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 2,
    letterSpacing: 0.5,
  },
  accountName: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
});
